import React from 'react'
import { Bar, Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import 'chart.js/auto'

const chartOptions = (stacked = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: 'index' },
  plugins: {
    legend: {
      labels: {
        color: '#cbd5f5',
        usePointStyle: true,
        padding: 16,
        pointStyle: 'rectRounded',
      },
    },
    tooltip: {
      backgroundColor: 'rgba(15,17,28,0.92)',
      titleColor: '#f8fafc',
      bodyColor: '#e2e8f0',
      borderColor: 'rgba(148, 163, 184, 0.25)',
      borderWidth: 1,
      displayColors: false,
      padding: 12,
      cornerRadius: 12,
    },
  },
  scales: {
    x: {
      stacked,
      ticks: { color: '#94a3b8', maxRotation: 0, font: { size: 11 } },
      grid: { color: 'rgba(148, 163, 184, 0.12)' },
    },
    y: {
      stacked,
      beginAtZero: true,
      ticks: { color: '#94a3b8', precision: 0 },
      grid: { color: 'rgba(148, 163, 184, 0.12)' },
    },
  },
})

const dateLabel = (value) =>
  new Date(value).toLocaleDateString('ru-KZ', {
    timeZone: 'Asia/Almaty',
    day: '2-digit',
    month: '2-digit',
  })

const LineChart = ({ labels, datasets, aria }) => (
  <div className="h-56 w-full" role="img" aria-label={aria}>
    <Line data={{ labels, datasets }} options={chartOptions(false)} />
  </div>
)

const BarChart = ({ labels, datasets, aria, stacked }) => (
  <div className="h-56 w-full" role="img" aria-label={aria}>
    <Bar data={{ labels, datasets }} options={chartOptions(stacked)} />
  </div>
)

const AssistantChart = ({ chart }) => {
  const { t } = useTranslation()
  const series = chart?.series
  if (!chart?.type || !Array.isArray(series) || series.length === 0) {
    return <p className="text-sm text-muted-400">{t('assistant.noChart')}</p>
  }

  if (chart.type === 'school_availability') {
    return (
      <LineChart
        aria={t('assistant.chartAria')}
        labels={series.map((p) => dateLabel(p.t))}
        datasets={[
          {
            label: t('assistant.available'),
            data: series.map((p) => p.available),
            borderColor: 'rgb(42, 209, 0)',
            backgroundColor: 'rgba(42, 209, 0, 0.15)',
            tension: 0.3,
            fill: false,
          },
          {
            label: t('assistant.unavailable'),
            data: series.map((p) => p.unavailable),
            borderColor: 'rgb(255, 77, 79)',
            backgroundColor: 'rgba(255, 77, 79, 0.15)',
            tension: 0.3,
            fill: false,
          },
        ]}
      />
    )
  }

  if (chart.type === 'region_availability') {
    return (
      <BarChart
        stacked
        aria={t('assistant.chartRegion')}
        labels={series.map((p) => p.region)}
        datasets={[
          {
            label: t('assistant.available'),
            data: series.map((p) => p.available),
            backgroundColor: 'rgba(42, 209, 0, 0.75)',
          },
          {
            label: t('assistant.unavailable'),
            data: series.map((p) => p.unavailable),
            backgroundColor: 'rgba(255, 77, 79, 0.75)',
          },
        ]}
      />
    )
  }

  if (chart.type === 'equipment_fleet' || chart.type === 'device_reachability') {
    const labelFor = (kind) => {
      if (kind === 'nvr') return t('assistant.nvrs')
      if (kind === 'panic' || kind === 'station') return t('assistant.stations')
      if (kind === 'fire') return t('assistant.firePanels')
      return t('assistant.turnstiles')
    }
    const isFleet = chart.type === 'equipment_fleet'
    return (
      <BarChart
        stacked
        aria={t(isFleet ? 'assistant.chartEquipment' : 'assistant.chartDevices')}
        labels={series.map((p) => labelFor(p.kind))}
        datasets={[
          {
            label: t('assistant.reachable'),
            data: series.map((p) => p.available),
            backgroundColor: 'rgba(42, 209, 0, 0.75)',
          },
          {
            label: t('assistant.unreachable'),
            data: series.map((p) => p.unavailable),
            backgroundColor: 'rgba(255, 77, 79, 0.75)',
          },
          ...(isFleet
            ? [
                {
                  label: t('assistant.noData'),
                  data: series.map((p) => p.noData || 0),
                  backgroundColor: 'rgba(148, 163, 184, 0.55)',
                },
                {
                  label: t('assistant.pollingDisabled'),
                  data: series.map((p) => p.pollingDisabled || 0),
                  backgroundColor: 'rgba(100, 116, 139, 0.45)',
                },
              ]
            : []),
        ]}
      />
    )
  }

  if (chart.type === 'traffic_utilization') {
    if (chart.grain === 'school' || series.some((p) => p.school)) {
      return (
        <BarChart
          aria={t('assistant.chartTraffic')}
          labels={series.map((p) => p.school)}
          datasets={[
            {
              label: t('assistant.trafficInGb'),
              data: series.map((p) => p.trafficInGb ?? 0),
              backgroundColor: 'rgba(64, 230, 255, 0.75)',
            },
            {
              label: t('assistant.trafficOutGb'),
              data: series.map((p) => p.trafficOutGb ?? 0),
              backgroundColor: 'rgba(110, 88, 255, 0.75)',
            },
          ]}
        />
      )
    }
    return (
      <LineChart
        aria={t('assistant.chartTraffic')}
        labels={series.map((p) => dateLabel(p.t))}
        datasets={[
          {
            label: t('assistant.utilization'),
            data: series.map((p) => p.utilization),
            borderColor: 'rgb(110, 88, 255)',
            backgroundColor: 'rgba(110, 88, 255, 0.15)',
            tension: 0.3,
            yAxisID: 'y',
            fill: false,
          },
          {
            label: t('assistant.trafficGb'),
            data: series.map((p) => p.trafficGb),
            borderColor: 'rgb(64, 230, 255)',
            backgroundColor: 'rgba(64, 230, 255, 0.15)',
            tension: 0.3,
            fill: false,
          },
        ]}
      />
    )
  }

  if (chart.type === 'speedtest_trend') {
    return (
      <LineChart
        aria={t('assistant.chartSpeedtest')}
        labels={series.map((p) => dateLabel(p.t))}
        datasets={[
          {
            label: t('assistant.download'),
            data: series.map((p) => p.download),
            borderColor: 'rgb(64, 230, 255)',
            tension: 0.3,
            fill: false,
          },
          {
            label: t('assistant.upload'),
            data: series.map((p) => p.upload),
            borderColor: 'rgb(255, 114, 210)',
            tension: 0.3,
            fill: false,
          },
        ]}
      />
    )
  }

  return <p className="text-sm text-muted-400">{t('assistant.noChart')}</p>
}

export default AssistantChart
